/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import CIcon from '@coreui/icons-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { cilLockLocked } from '@coreui/icons';
import { faFileWord } from '@fortawesome/free-solid-svg-icons';

const getFileCategory = (src) => {
  if (typeof src !== 'string') return null;
  if (src?.includes('pdf-placeholder')) return 'pdf';
  if (src.startsWith('data:')) {
    const mime = src.slice(5, src.indexOf(';'));
    if (mime === 'application/pdf') return 'pdf';
    if (mime.includes('image/')) return 'image';
    if (mime.includes('word')) return 'word';
    return 'unknown';
  }

  const ext = src.split('.').pop()?.toLowerCase();
  if (!ext) return null;

  if (ext === 'pdf') return 'pdf';
  if (ext === 'doc' || ext === 'docx') return 'word';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return 'image';

  return 'unknown';
};

const CustomInput = ({
  icon,
  type,
  label,
  title,
  id,
  name,
  placeholder,
  value,
  isRequired,
  disabled,
  onChange,
  className,
  onIconClick,
  onClick,
  readOnly,
  message,
  pattern,
  maxLength,
  defaultValue,
  inputRef,
  width,
  onKeyDown,
  autoFocus,
  onBlur,
  accept,
  helperText,
  minLength,
  src,
  labelClass,
  labelNote,
  mfa,
  onMfaClick,
  step,
  max,
  min,
}) => {
  const [fileError, setFileError] = useState('');

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (isRequired && (!files || files.length === 0)) {
      setFileError(message || 'This field is required');
    } else {
      setFileError('');
      onChange && onChange(e);
    }
  };

  /**
   * For number inputs, clamp the typed value between min and max
   * before forwarding the event to the parent's onChange.
   * This enforces limits on every keystroke, not just on submit.
   */
  const handleNumberChange = (e) => {
    if (!onChange) return;

    const raw = e.target.value;

    // Allow empty string so the user can clear the field
    if (raw === '' || raw === '-') {
      onChange(e);
      return;
    }

    let numeric = parseFloat(raw);

    if (isNaN(numeric)) {
      onChange(e);
      return;
    }

    // Clamp between min and max when both (or either) are defined
    if (min !== undefined && numeric < min) numeric = min;
    if (max !== undefined && numeric > max) numeric = max;

    // Mutate the synthetic event value so the parent sees the clamped number
    const clampedEvent = {
      ...e,
      target: {
        ...e.target,
        value: String(numeric),
        name: e.target.name,
        type: e.target.type,
        checked: e.target.checked,
      },
    };

    onChange(clampedEvent);
  };

  // Drag and drop handlers for file input only
  const handleDragEnter = (e) => {
    if (type !== 'file' || disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    if (type !== 'file' || disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragOver = (e) => {
    if (type !== 'file' || disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    if (type !== 'file' || disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const syntheticEvent = {
        target: {
          files: files,
          type,
          name: name,
          id: id,
        },
      };
      handleFileChange(syntheticEvent);

      if (inputRef && inputRef.current) {
        const dataTransfer = new DataTransfer();
        Array.from(files).forEach((file) => dataTransfer.items.add(file));
        inputRef.current.files = dataTransfer.files;
      }
    }
  };

  // Pick the right onChange handler based on input type
  const resolveOnChange = () => {
    if (type === 'file') return handleFileChange;
    if (type === 'number') return handleNumberChange;
    return (e) => onChange && onChange(e);
  };

  return (
    <div
      className={`form-outline text-start ${label ? 'mt-2' : icon ? '' : width ? width : 'w-30'} `}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {label && (
        <label htmlFor={id} className={`login_label labelName mb-2 ${labelClass}`}>
          {label} {labelNote && <span className="label-note-warning">{labelNote}</span>}
        </label>
      )}

      <div className={`${label ? 'input-group' : ''} position-relative`}>
        {icon && (
          <span
            className="input-group-addon pointer"
            title={title || label || ''}
            onClick={() => onIconClick && onIconClick()}
          >
            <CIcon
              className={isRequired ? 'mandatory-control' : 'stock-toggle-icon '}
              icon={icon}
            />
          </span>
        )}

        <input
          ref={inputRef ?? inputRef}
          type={type}
          className={`${className} ${helperText ? 'w-10 ' : ''} form-control item`}
          id={id}
          name={name}
          value={type === 'file' ? undefined : value ?? ''}
          placeholder={placeholder}
          required={isRequired && type !== 'file'}
          disabled={disabled || false}
          autoComplete="new-password"
          onChange={resolveOnChange()}
          autoFocus={autoFocus ?? false}
          onClick={() => onClick && onClick()}
          minLength={minLength}
          accept={accept}
          readOnly={readOnly || false}
          pattern={pattern}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          defaultValue={defaultValue}
          onBlur={onBlur}
          step={step}
          max={max}
          min={min}
        />

        {mfa && (
          <span
            className="input-group-addon-mfa pointer"
            onClick={() => onMfaClick && onMfaClick()}
            title="Two Factor Authentication"
          >
            <CIcon className={'stock-toggle-icon'} icon={cilLockLocked} />
          </span>
        )}

        {isRequired && type !== 'file' && <span className="invalid-tooltip">{message}</span>}

        {type === 'file' && (
          <div className="form-control row me-0">
            <div className="col-8 text-truncate ps-0">{helperText}</div>
          </div>
        )}

        {type === 'file' &&
          (() => {
            const fileType = getFileCategory(src);
            if (!fileType) return null;

            if (fileType === 'pdf') {
              return (
                <FontAwesomeIcon icon={faFilePdf} size="lg" className="input-file-image-preview" />
              );
            }

            if (fileType === 'word') {
              return (
                <FontAwesomeIcon icon={faFileWord} size="lg" className="input-file-image-preview" />
              );
            }

            return <img src={src} className="input-file-image-preview" alt="preview" />;
          })()}
      </div>
    </div>
  );
};

export default CustomInput;
